"use client";

/**
 * @module Input
 *
 * Styled text input with support for icons, sizes, and clear button.
 * Includes focus state management and dark mode support.
 *
 * @example
 * // Basic input
 * <Input placeholder="Enter your email" type="email" />
 *
 * @example
 * // With icons
 * <Input
 *   leftIcon={<Search />}
 *   rightIcon={<ClearInputButton onClick={clearValue} />}
 *   placeholder="Search..."
 * />
 *
 * @example
 * // Small size
 * <Input size="sm" placeholder="Compact input" />
 *
 * @example
 * // With clear button
 * const [value, setValue] = useState('');
 *
 * <Input
 *   value={value}
 *   onChange={(e) => setValue(e.target.value)}
 *   rightIcon={
 *     value && <ClearInputButton onClick={() => setValue('')} />
 *   }
 * />
 */
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { Slot as SlotPrimitive } from "radix-ui";
import type { Ref } from "react";
import { cn } from "../lib/utils";
const inputWrapperVariants = cva("relative flex w-full items-center", {
  variants: {
    size: {
      auto: "h-auto w-auto",
      default: "h-10",
      sm: "h-7"
    }
  },
  defaultVariants: {
    size: "default"
  }
});
const inputVariants = cva(["w-full touch-manipulation self-stretch shadow-none outline-hidden outline-0 transition duration-200 ease-out disabled:cursor-not-allowed dark:disabled:cursor-not-allowed",
// text
"font-sans text-base text-black antialiased disabled:text-black/60 dark:text-white dark:disabled:text-white/60",
// placeholder
"placeholder-gray-700 placeholder:font-sans focus:placeholder:text-transparent dark:placeholder-gray-200 dark:focus:placeholder:text-transparent",
// background — transparent across interactive states so the input
// inherits whatever surface it sits on (page, card, modal, drawer).
// Disabled keeps a faded fill so the state stays distinguishable.
"bg-transparent focus:bg-transparent enabled:hover:bg-transparent disabled:bg-white/60 dark:disabled:bg-black/60",
// borders (light)
"rounded-lg border border-gray-150 border-solid invalid:border-red invalid:hover:border-red focus:border-brand-primary enabled:hover:border-gray-200 enabled:focus:hover:border-brand-primary disabled:border-gray-150/60",
// borders light
// borders (dark)
"dark:border-gray-800 dark:disabled:border-gray-900 dark:focus:border-white dark:enabled:hover:border-gray-700 dark:enabled:focus:hover:border-white dark:invalid:border-red dark:invalid:hover:border-red" // borders dark
], {
  variants: {
    size: {
      default: "px-4",
      sm: "px-3",
      auto: "px-4"
    },
    hasLeftIcon: {
      true: "",
      false: ""
    },
    hasRightIcon: {
      true: "",
      false: ""
    },
    // Scoped to type="search" only: the Keeper password manager extension
    // misidentifies inputs that style ::-webkit-search-cancel-button as
    // search fields and blocks paste, so non-search inputs must not match.
    type: {
      search: "dark:[&::-webkit-search-cancel-button]:hidden"
    }
  },
  compoundVariants: [{
    hasLeftIcon: true,
    size: "default",
    class: "pl-10"
  }, {
    hasLeftIcon: true,
    size: "sm",
    class: "pl-8"
  }, {
    hasRightIcon: true,
    size: "default",
    class: "pr-10"
  }, {
    hasRightIcon: true,
    size: "sm",
    class: "pr-8"
  }],
  defaultVariants: {
    size: "default",
    hasLeftIcon: false,
    hasRightIcon: false
  }
});
type InputPropsWithoutSize = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">;
type InputVariantsProps = VariantProps<typeof inputVariants>;

// Omit internal-only variants from the public API. `type` is excluded so
// consumers keep the full HTMLInputTypeAttribute union; CVA still picks up
// the runtime value via the `type` argument passed in inputVariants().
type PublicInputVariantsProps = Omit<InputVariantsProps, "hasLeftIcon" | "hasRightIcon" | "type">;

/**
 * Props for the Input component.
 * @property size - Input size: `"default"`, `"sm"`, or `"auto"`
 * @property htmlSize - Native HTML size attribute for input width
 * @property leftIcon - Icon element displayed on the left side
 * @property rightIcon - Icon element displayed on the right side
 */
export interface InputProps extends InputPropsWithoutSize, PublicInputVariantsProps {
  htmlSize?: number;
  leftIcon?: React.ReactNode;
  ref?: Ref<HTMLInputElement>;
  rightIcon?: React.ReactNode;
}
const Input = ({
  className,
  size,
  type = "text",
  htmlSize,
  leftIcon,
  rightIcon,
  ref,
  ...props
}: InputProps) => <div className={cn(inputWrapperVariants({
  size
}))} data-sentry-component="Input" data-sentry-source-file="input.tsx">
    {!!leftIcon && <SlotPrimitive.Slot className={cn("absolute top-1/2 -translate-y-1/2 text-gray-950 dark:text-gray-300 [&:is(svg)]:pointer-events-none", size === "sm" ? "left-3 size-3" : "left-4 size-4")}>
        {leftIcon}
      </SlotPrimitive.Slot>}
    <input className={cn(inputVariants({
    size,
    hasLeftIcon: !!leftIcon,
    hasRightIcon: !!rightIcon,
    type: type === "search" ? "search" : undefined,
    className
  }))} ref={ref} size={htmlSize} type={type} {...props} />
    {!!rightIcon && <SlotPrimitive.Slot className={cn("absolute top-1/2 -translate-y-1/2 text-gray-950 dark:text-gray-300 [&:is(svg)]:pointer-events-none", size === "sm" ? "right-3 size-3" : "right-4 size-4")}>
        {rightIcon}
      </SlotPrimitive.Slot>}
  </div>;
interface ClearInputButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  ref?: Ref<HTMLButtonElement>;
}

/**
 * Clear/reset button designed for use as Input's rightIcon.
 * Displays an X icon and handles focus states.
 */
const ClearInputButton = ({
  className,
  ref,
  ...props
}: ClearInputButtonProps) => <button className={cn("absolute top-1/2 -translate-y-1/2 text-gray-950 dark:text-gray-300", "focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-gray-100", "rounded-full focus-visible:outline-hidden dark:focus-visible:ring-[var(--focus-ring)] dark:focus-visible:ring-offset-gray-800", className)} ref={ref} type="button" {...props} data-sentry-component="ClearInputButton" data-sentry-source-file="input.tsx">
    <X className="absolute inset-0 size-full" strokeWidth={3} data-sentry-element="X" data-sentry-source-file="input.tsx" />
  </button>;
export { ClearInputButton, Input, inputVariants };